import { useState } from "react";
import type { ChargerFilterKey, ChargerSortDir, ChargerSortKey } from "../../types/charger";
import { formatChargerIdsClipboard, toggleFilter, toggleSort } from "../../utils/chargerFilterSort";

const SORT_OPTIONS: { key: ChargerSortKey; label: string }[] = [
  { key: "id", label: "ID" },
  { key: "health", label: "Health" },
  { key: "mv5", label: "MV5" },
  { key: "fcr", label: "LB" },
];

const FILTER_OPTIONS: { key: ChargerFilterKey; label: string; group?: string }[] = [
  { key: "ev", label: "Psd", group: "ring" },
  { key: "chg", label: "Chg", group: "ring" },
  { key: "health_ok", label: "H ok", group: "health" },
  { key: "health_new", label: "H new", group: "health" },
  { key: "health_stale", label: "H stale", group: "health" },
  { key: "mv5", label: "MV5" },
  { key: "act", label: "LB", group: "fcr" },
  { key: "sp", label: "SP" },
  { key: "resp", label: "Resp" },
];

export function ChargerControls({
  filteredCount,
  totalCount,
  filteredLabels,
  filters,
  sortKey,
  sortDir,
  searchQuery,
  searchMatchCount,
  onSearchChange,
  onFiltersChange,
  onSortChange,
}: {
  filteredCount: number;
  totalCount: number;
  filteredLabels: string[];
  filters: Set<ChargerFilterKey>;
  sortKey: ChargerSortKey;
  sortDir: ChargerSortDir;
  searchQuery: string;
  searchMatchCount: number | null;
  onSearchChange: (query: string) => void;
  onFiltersChange: (filters: Set<ChargerFilterKey>) => void;
  onSortChange: (key: ChargerSortKey, dir: ChargerSortDir) => void;
}) {
  const [copyHint, setCopyHint] = useState<string | null>(null);

  const onCopyIds = async () => {
    const text = formatChargerIdsClipboard(filteredLabels);
    try {
      await navigator.clipboard.writeText(text);
      setCopyHint(`Copied ${filteredLabels.length}`);
    } catch {
      setCopyHint("Copy failed");
    }
    window.setTimeout(() => setCopyHint(null), 2000);
  };

  const clearFilters = () => onFiltersChange(new Set());

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Sort chargers">
          <span className="text-text-tertiary" style={{ fontSize: "var(--font-micro)" }}>
            Sort
          </span>
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={[
                "charger-chip-btn",
                sortKey === opt.key ? "charger-chip-btn--active" : "",
              ].join(" ")}
              onClick={() => {
                const next = toggleSort(sortKey, sortDir, opt.key);
                onSortChange(next.key, next.dir);
              }}
              title={sortKey === opt.key ? `Sorted ${sortDir}` : `Sort by ${opt.label}`}
            >
              {opt.label}
              {sortKey === opt.key ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1.5">
            <span className="text-text-tertiary" style={{ fontSize: "var(--font-micro)" }}>
              Find
            </span>
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="BCU…"
              className="w-28 rounded border border-border-subtle bg-page px-2 py-0.5 font-mono text-text-primary outline-none focus:border-accent sm:w-36"
              style={{ fontSize: "var(--font-micro)" }}
              spellCheck={false}
              aria-label="Find charger by ID"
            />
            {searchMatchCount != null && (
              <span className="text-text-tertiary" style={{ fontSize: "var(--font-micro)" }}>
                {searchMatchCount}
              </span>
            )}
          </label>
          <button
            type="button"
            className="charger-chip-btn charger-chip-btn--accent"
            onClick={onCopyIds}
            disabled={!filteredLabels.length}
            title="Copy filtered charger IDs — comma-separated, 10 per line"
          >
            Copy IDs ({filteredCount})
          </button>
          {copyHint && (
            <span className="text-status-ok" style={{ fontSize: "var(--font-micro)" }}>
              {copyHint}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Filter chargers">
        <span className="text-text-tertiary" style={{ fontSize: "var(--font-micro)" }}>
          Filter
        </span>
        {FILTER_OPTIONS.map((opt) => (
          <button
            key={opt.key}
            type="button"
            className={[
              "charger-chip-btn",
              filters.has(opt.key) ? "charger-chip-btn--active" : "",
            ].join(" ")}
            onClick={() => onFiltersChange(toggleFilter(filters, opt.key))}
          >
            {opt.label}
          </button>
        ))}
        {filters.size > 0 && (
          <button
            type="button"
            className="charger-chip-btn charger-chip-btn--ghost"
            onClick={clearFilters}
          >
            Clear
          </button>
        )}
        <span className="text-text-tertiary" style={{ fontSize: "var(--font-micro)" }}>
          · {filteredCount}/{totalCount}
        </span>
      </div>
    </div>
  );
}
